<template>
  <div class="container mx-auto px-4 py-8">
    <div class="flex justify-between items-start mb-8">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">ENISA Reporting</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">CRA Article 14 — Incident & vulnerability notification management</p>
      </div>
    </div>

    <!-- Deadline Banner -->
    <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-5 py-4 mb-6">
      <div class="flex items-start gap-3">
        <svg class="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
        <div>
          <p class="text-sm font-semibold text-red-800 dark:text-red-300">Reporting obligation starts September 11, 2026</p>
          <p class="text-xs text-red-600 dark:text-red-400 mt-0.5">Actively exploited vulnerabilities: 24h early warning → 72h full notification → 14d final report. Severe incidents: 24h → 72h → 30d.</p>
          <p class="text-xs text-red-600/70 dark:text-red-400/70 mt-1">Reports are currently prepared for manual submission. Direct submission to the ENISA single reporting platform will be available once their API launches.</p>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div v-if="dashboard" class="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
      <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-center">
        <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ dashboard.stats.total }}</p>
        <p class="text-xs text-gray-500 dark:text-gray-400">Total</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-center">
        <p class="text-2xl font-bold text-gray-600 dark:text-gray-300">{{ dashboard.stats.draft }}</p>
        <p class="text-xs text-gray-500 dark:text-gray-400">Draft</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-center">
        <p class="text-2xl font-bold text-blue-600 dark:text-blue-400">{{ dashboard.stats.active }}</p>
        <p class="text-xs text-gray-500 dark:text-gray-400">Active</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg border border-red-200 dark:border-red-800 p-4 text-center" :class="dashboard.stats.overdue > 0 ? 'bg-red-50 dark:bg-red-900/20' : ''">
        <p class="text-2xl font-bold" :class="dashboard.stats.overdue > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'">{{ dashboard.stats.overdue }}</p>
        <p class="text-xs text-gray-500 dark:text-gray-400">Overdue</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-center">
        <p class="text-2xl font-bold text-green-600 dark:text-green-400">{{ dashboard.stats.closed }}</p>
        <p class="text-xs text-gray-500 dark:text-gray-400">Closed</p>
      </div>
    </div>

    <!-- Tabs -->
    <div class="flex border-b border-gray-200 dark:border-gray-700 mb-6">
      <button v-for="t in tabs" :key="t.id" @click="activeTab = t.id"
        class="px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors"
        :class="activeTab === t.id ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'">
        {{ t.label }}
      </button>
    </div>

    <div v-if="loading" class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 dark:border-gray-700 border-t-blue-500"></div>
    </div>

    <!-- Incidents Tab -->
    <div v-else-if="activeTab === 'incidents'">
      <div v-if="!incidents.length" class="text-center py-12">
        <p class="text-gray-500 dark:text-gray-400">No incidents tracked yet.</p>
        <p class="text-sm text-gray-400 dark:text-gray-500 mt-1">Use "Report to ENISA" on a vulnerability or create an incident manually.</p>
      </div>

      <div v-else class="space-y-3">
        <div v-for="inc in incidents" :key="inc.id"
          class="bg-white dark:bg-gray-800 border rounded-lg p-4 hover:shadow-md transition-shadow"
          :class="inc.is_overdue ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'">
          <div class="flex items-start justify-between">
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2 mb-1">
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" :class="severityClass(inc.severity)">{{ inc.severity }}</span>
                <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium" :class="incStatusClass(inc.status)">{{ incStatusLabel(inc.status) }}</span>
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                  :class="inc.is_public ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'">
                  {{ inc.is_public ? 'Public' : 'Private' }}
                </span>
                <span v-if="inc.is_overdue" class="text-xs text-red-600 dark:text-red-400 font-semibold">OVERDUE</span>
              </div>
              <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ inc.title }}</p>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {{ inc.type === 'severe_incident' ? 'Severe Incident' : 'Actively Exploited Vulnerability' }}
                <template v-if="inc.product_name"> · {{ inc.product_name }}</template>
              </p>
            </div>
          </div>

          <!-- Timeline -->
          <div class="mt-3 flex gap-3 flex-wrap">
            <div class="flex items-center gap-1.5">
              <div class="w-2 h-2 rounded-full" :class="inc.early_warning_sent ? 'bg-green-500' : (isOverdue(inc.early_warning_due) ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600')"></div>
              <span class="text-xs text-gray-600 dark:text-gray-400">
                EW {{ inc.early_warning_sent ? '✓' : formatCountdown(inc.early_warning_due) }}
              </span>
            </div>
            <div class="flex items-center gap-1.5">
              <div class="w-2 h-2 rounded-full" :class="inc.full_notif_sent ? 'bg-green-500' : (isOverdue(inc.full_notif_due) ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600')"></div>
              <span class="text-xs text-gray-600 dark:text-gray-400">
                FN {{ inc.full_notif_sent ? '✓' : formatCountdown(inc.full_notif_due) }}
              </span>
            </div>
            <div class="flex items-center gap-1.5">
              <div class="w-2 h-2 rounded-full" :class="inc.final_report_sent ? 'bg-green-500' : (isOverdue(inc.final_report_due) ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600')"></div>
              <span class="text-xs text-gray-600 dark:text-gray-400">
                FR {{ inc.final_report_sent ? '✓' : formatCountdown(inc.final_report_due) }}
              </span>
            </div>
          </div>

          <!-- Report Generation Buttons -->
          <div class="mt-3 flex gap-2 flex-wrap">
            <button @click="generateReport(inc.id, 'early-warning')" class="text-xs px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              Early Warning JSON
            </button>
            <button @click="generateReport(inc.id, 'full-notification')" class="text-xs px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              Full Notification JSON
            </button>
            <button @click="generateReport(inc.id, 'final-report')" class="text-xs px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              Final Report JSON
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Legacy Vuln Reports Tab -->
    <div v-else-if="activeTab === 'vulns'">
      <div v-if="!vulns.length" class="text-center py-12">
        <p class="text-gray-500 dark:text-gray-400">No vulnerabilities to generate reports for.</p>
      </div>
      <div v-else class="space-y-3">
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">Generate standalone vulnerability notification reports (for vulns not yet tracked as incidents).</p>
        <div v-for="v in vulns" :key="v.id"
          class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 flex items-center justify-between cursor-pointer hover:shadow-md transition-shadow"
          @click="generateVulnReport(v.id)">
          <div class="flex items-center gap-3 min-w-0">
            <span class="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium" :class="severityClass(v.severity)">{{ v.severity }}</span>
            <div class="min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ v.cve_id || v.synopsis }}</p>
              <p v-if="v.cve_id" class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ v.synopsis }}</p>
            </div>
          </div>
          <svg class="w-4 h-4 text-gray-400 flex-shrink-0 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
        </div>
      </div>
    </div>

    <!-- Report Preview Modal -->
    <div v-if="reportData" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50" @click.self="reportData = null">
      <div class="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col mx-4">
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white">{{ reportData.report_type || 'ENISA Report' }}</h2>
            <p class="text-xs text-gray-500 dark:text-gray-400">{{ reportData.report_id }}</p>
          </div>
          <div class="flex items-center gap-2">
            <button @click="downloadJSON" class="btn-secondary text-sm">Download JSON</button>
            <button @click="copyReport" class="btn-secondary text-sm">Copy</button>
            <button @click="reportData = null" class="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
              <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
        </div>
        <div class="flex-1 overflow-y-auto p-6">
          <pre class="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono bg-gray-50 dark:bg-gray-800 rounded-lg p-4">{{ formattedReport }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import axios from 'axios'

const getAPIURL = () => {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  const cleanUrl = envUrl.replace(/\/$/, '')
  return cleanUrl.endsWith('/api/v1') ? cleanUrl : `${cleanUrl}/api/v1`
}

export default {
  name: 'ENISAReportingView',
  setup() {
    const store = useStore()
    const route = useRoute()
    const tenantId = computed(() => route.params.tenantId || store.getters.currentTenantId)
    const loading = ref(false)
    const dashboard = ref(null)
    const incidents = ref([])
    const vulns = ref([])
    const reportData = ref(null)
    const activeTab = ref('incidents')
    const tabs = [
      { id: 'incidents', label: 'Incident Dashboard' },
      { id: 'vulns', label: 'Vulnerability Reports' }
    ]

    const headers = computed(() => ({ Authorization: `Bearer ${store.state.token}` }))

    const fetchDashboard = async () => {
      if (!tenantId.value) return
      loading.value = true
      try {
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId.value}/enisa/templates`, { headers: headers.value })
        dashboard.value = res.data
        incidents.value = res.data.incidents || []
      } catch (e) {
        console.error('Failed to fetch ENISA dashboard:', e)
      } finally {
        loading.value = false
      }
    }

    const fetchVulns = async () => {
      try {
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId.value}/vulns`, {
          headers: headers.value,
          params: { per_page: 100, order_by: 'id', sort_direction: 'desc' }
        })
        vulns.value = (res.data?.data || res.data || []).map(v => ({
          id: v.id, cve_id: v.cve_id, synopsis: v.synopsis, severity: v.severity, status: v.status
        }))
      } catch (e) {
        console.error('Failed to fetch vulns:', e)
      }
    }

    const generateReport = async (incidentId, stage) => {
      try {
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId.value}/enisa/incidents/${incidentId}/${stage}`, { headers: headers.value })
        reportData.value = res.data
      } catch (e) {
        store.dispatch('notification/showNotification', { type: 'error', message: 'Failed to generate report' })
      }
    }

    const generateVulnReport = async (vulnId) => {
      try {
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId.value}/enisa/vuln-report/${vulnId}`, { headers: headers.value })
        reportData.value = res.data
      } catch (e) {
        store.dispatch('notification/showNotification', { type: 'error', message: 'Failed to generate report' })
      }
    }

    const formattedReport = computed(() => reportData.value ? JSON.stringify(reportData.value, null, 2) : '')

    const downloadJSON = () => {
      if (!reportData.value) return
      const blob = new Blob([JSON.stringify(reportData.value, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${reportData.value.report_id || 'enisa-report'}.json`
      a.click()
      URL.revokeObjectURL(url)
    }

    const copyReport = async () => {
      try {
        await navigator.clipboard.writeText(JSON.stringify(reportData.value, null, 2))
        store.dispatch('notification/showNotification', { type: 'success', message: 'Copied to clipboard' })
      } catch {
        store.dispatch('notification/showNotification', { type: 'error', message: 'Failed to copy' })
      }
    }

    const severityClass = (sev) => ({
      CRITICAL: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      HIGH: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      MEDIUM: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      LOW: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
    }[sev] || 'bg-gray-100 text-gray-800')

    const incStatusClass = (s) => ({
      draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      early_warning_sent: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      full_notification_sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      final_report_sent: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      closed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    }[s] || '')

    const incStatusLabel = (s) => ({
      draft: 'Draft', early_warning_sent: 'Early Warning', full_notification_sent: 'Full Notification',
      final_report_sent: 'Final Report', closed: 'Closed'
    }[s] || s)

    const isOverdue = (d) => d && new Date(d) < new Date()

    const formatCountdown = (d) => {
      if (!d) return '—'
      const diff = new Date(d) - new Date()
      if (diff < 0) return 'overdue'
      const hours = Math.floor(diff / 3600000)
      if (hours < 48) return `${hours}h`
      return `${Math.floor(hours / 24)}d`
    }

    onMounted(() => { fetchDashboard(); fetchVulns() })

    return {
      tabs, activeTab, loading, dashboard, incidents, vulns, reportData, formattedReport,
      generateReport, generateVulnReport, downloadJSON, copyReport,
      severityClass, incStatusClass, incStatusLabel, isOverdue, formatCountdown
    }
  }
}
</script>
